import { onIdTokenChanged } from 'firebase/auth';
import { auth } from '../firebase/config';
import { FUNCTION_BASE } from './api';
import { storedArrival } from './campaign';
import {
  PRESS_SELECTOR, SCRIPTURE_PRESS, GOALS, VIEWS, describeClick, buildAttribution, buildVisit, referrerDomain, buildVoiceEvent, cleanPlaceAnswer,
} from './analyticsCore';
import { buildXpEvent, ID_PATTERN } from './experimentsCore';

/**
 * Our own visitor counts (functions/analytics.js; docs/ADMIN_PORTAL.md, "Analytics").
 *
 * What it keeps: a random visitor ID and a per-tab session ID, whether this
 * browser has been here before, the Central day it last reported (so only its
 * first batch of a day counts the visitor), the screen someone is on, the
 * labels of our own buttons they press, our own starter questions, how many
 * questions they ask, how each reading of Rob's voice went (why it started,
 * how long until it spoke, what ended it, the seconds heard), whether this
 * browser has heard his voice before, a coarse place (country, or US state and
 * county, never finer) our host gives for the connection, for 12 hours, and the
 * one this tab's visit went out with, and the moments the two A/B tests
 * count (trackXp: the test, its key, the group, the moment; utils/experiments.js and
 * docs/plans/experiments.md). What it never touches: anything typed,
 * anything AI Rob answers, names, emails, saved-chat titles. The rules for
 * that are in analyticsCore.js and are checked by scripts/check-analytics.js.
 *
 * It must never matter to the app. Everything here is wrapped so a failure is
 * silent; blocked storage, a blocked or missing endpoint, and a browser with
 * no sendBeacon all just mean fewer counts. It does not run on a local
 * address (localhost, 127.0.0.1: the dev server and the phone harness, which
 * would otherwise post to production), for bots, or for anyone who sends Do
 * Not Track or Global Privacy Control.
 *
 * Events wait in a queue and go out in batches of at most 20: every 10 seconds,
 * and when the page is hidden or closed (sendBeacon, so a closing tab still
 * reports). No cookies: the IDs live in localStorage and sessionStorage.
 */

const ENDPOINT = `${FUNCTION_BASE}/track`;
const SEND_EVERY_MS = 10000;
const FIRST_SEND_MS = 4000;
const MAX_QUEUE = 200;
const MAX_BATCH = 20;
const GIVE_UP_AFTER = 6; // failed sends in a row, then stop for this page load
const VISITOR_KEY = 'apr-vid';
const SESSION_KEY = 'apr-sid';
const RETURNING_KEY = 'apr-sid-returning';
const ASKS_KEY = 'apr-asks';
const INTERNAL_KEY = 'apr-internal';
// The visitor's country or US state (functions-place/), asked at the function's own URL (a Hosting
// rewrite would lose the location). On since Oct 17, 2026 (Alexi's go); empty would be off, and
// deleting the function switches it off again with no rebuild (docs/ADMIN_PORTAL.md).
const PLACE_ENDPOINT = 'https://us-central1-askpastorrob-b6812.cloudfunctions.net/visitorPlace';
const PLACE_KEY = 'apr-place';            // { g, c, at } in localStorage, kept 12 hours ({} answers too)
const PLACE_KEEP_MS = 12 * 3600 * 1000;
const PLACE_START_MS = 1000;              // after first paint, never in its way
const PLACE_WAIT_MS = 2500;               // settled by 3.5 s, before the first send at 4 s
const SESSION_PLACE_KEY = 'apr-sid-place'; // { g, c } in sessionStorage: the place this tab's visit went out with
const DAY_KEY = 'apr-day';                // the Central day this browser last sent a batch
const HEARD_KEY = 'apr-av';               // "1" once this browser has played Rob's voice (utils/voice.js)

const state = {
  started: false,
  off: false,
  queue: [],
  visitorId: '',
  sessionId: '',
  returning: false,
  internal: false,
  signedIn: false,
  sawGuest: false,
  authSeen: false,
  token: null,
  attribution: {},
  place: '',
  county: '',
  sessionPlace: null,
  lastDay: '',
  lastView: null,
  asks: 0,
  failures: 0,
  retryAt: 0,
  timer: null,
};

function read(storage, key) {
  try { return storage.getItem(key); } catch (e) { return null; }
}

function write(storage, key, value) {
  try { storage.setItem(key, value); } catch (e) { /* storage blocked: this page load only */ }
}

function randomId() {
  try {
    const bytes = new Uint8Array(16);
    crypto.getRandomValues(bytes);
    return btoa(String.fromCharCode(...bytes)).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
  } catch (e) {
    return `${Date.now().toString(36)}${Math.random().toString(36).slice(2)}${Math.random().toString(36).slice(2)}`.slice(0, 40);
  }
}

/**
 * Only real visitors, who have not asked not to be counted. A local address (the dev server, the
 * phone harness) never reports: it would post to production. Only the harness sets
 * __APR_TRACK_TEST__, and it answers the track address itself.
 */
function shouldRun() {
  if (typeof window === 'undefined' || typeof document === 'undefined') return false;
  const host = window.location.hostname;
  const local = host === 'localhost' || host === '127.0.0.1' || host === '[::1]' || host === '';
  if (local && !window.__APR_TRACK_TEST__) return false;
  const nav = window.navigator || {};
  if (nav.globalPrivacyControl === true || nav.doNotTrack === '1' || window.doNotTrack === '1') return false;
  // Only the crawlers that run scripts; the server turns away the wider list of bots.
  if (/bot\b|crawl|spider|slurp/i.test(nav.userAgent || '')) return false;
  return typeof fetch === 'function';
}

/**
 * `?apr_internal=1` marks this browser as the team's: its visits are counted
 * apart from the public's. `?apr_internal=0` undoes it. The address is cleaned
 * so the flag does not stay in the bar or in a shared link.
 */
function readInternalFlag() {
  try {
    const url = new URL(window.location.href);
    const flag = url.searchParams.get('apr_internal');
    if (flag !== null) {
      if (flag === '1') write(window.localStorage, INTERNAL_KEY, '1');
      else { try { window.localStorage.removeItem(INTERNAL_KEY); } catch (e) { /* ignore */ } }
      url.searchParams.delete('apr_internal');
      window.history.replaceState(window.history.state, '', `${url.pathname}${url.search}${url.hash}`);
    }
  } catch (e) { /* ignore */ }
  return read(window.localStorage, INTERNAL_KEY) === '1';
}

/**
 * Whether this browser is marked as the team's (?apr_internal=1), read even when counting is off:
 * Rob's voice sends it with each request (int), so the server counts a team phone apart and never
 * puts it in the voice test. It grants nothing.
 */
export function isInternalBrowser() {
  try { return window.localStorage.getItem(INTERNAL_KEY) === '1'; } catch (e) { return false; }
}

/** Whether this browser has played Rob's voice before (apr-av, set by utils/voice.js). */
function heardVoice() {
  try { return window.localStorage.getItem(HEARD_KEY) === '1'; } catch (e) { return false; }
}

/**
 * The place a batch carries: the one this tab's visit went out with. The visit's own batch fixes it
 * for the session (kept in sessionStorage, so a reload keeps it), and every later batch carries the
 * same, so a question is counted where its visit was, even when a place arrives, changes or expires
 * after the visit went. A tab whose visit went out before this was kept carries the place it has.
 */
function placeFor(events) {
  if (events.some((e) => e.t === 'visit')) {
    state.sessionPlace = { g: state.place, c: state.county };
    write(window.sessionStorage, SESSION_PLACE_KEY, JSON.stringify(state.sessionPlace));
  }
  return state.sessionPlace || { g: state.place, c: state.county };
}

function envelope(events, first) {
  const place = placeFor(events);
  return JSON.stringify({
    v: 1,
    vid: state.visitorId,
    sid: state.sessionId,
    ret: state.returning,
    signedIn: state.signedIn,
    a: state.attribution,
    first,
    ...(place.g ? { g: place.g } : {}),
    ...(place.g && place.c ? { gc: place.c } : {}),
    ...(state.internal ? { int: true } : {}),
    ...(heardVoice() ? { av: true } : {}),
    ...(state.token ? { token: state.token } : {}),
    events,
  });
}

// Run on pagehide before the leaving flush, so what they queue goes out with it (a reading of
// Rob's voice that the closing page ended: utils/voice.js, by 'leave').
const leavingHooks = new Set();

/** fn() runs when the page closes, before the last batch is sent. Returns the way to remove it. */
export function onLeaving(fn) {
  leavingHooks.add(fn);
  return () => { leavingHooks.delete(fn); };
}

function runLeavingHooks() {
  leavingHooks.forEach((fn) => { try { fn(); } catch (e) { /* a hook never stops the flush */ } });
}

// Run when the page is hidden (another tab, another app, the screen off) or closed, before the
// batch that goes then: First Answer's "left within 15 seconds of the answer" (utils/experiments.js). A reading
// of Rob's voice goes on while the page is hidden, so it is never told (onLeaving is the close).
const hidingHooks = new Set();

/** fn() runs when the page is hidden or closed, before that moment's batch is sent. Returns the way to remove it. */
export function onHiding(fn) {
  hidingHooks.add(fn);
  return () => { hidingHooks.delete(fn); };
}

function runHidingHooks() {
  hidingHooks.forEach((fn) => { try { fn(); } catch (e) { /* a hook never stops the flush */ } });
}

/**
 * This browser's visitor id while it counts visits, else '': the unit of the two A/B tests
 * (utils/experiments.js). Empty for Do Not Track and Global Privacy Control, a local address, a
 * crawler, or blocked storage: those browsers are in neither test, and get today's version.
 */
export function countingVisitorId() {
  return state.started && !state.off && ID_PATTERN.test(state.visitorId) ? state.visitorId : '';
}

/** Today in Central time ("2026-10-16"), the day the counts are kept by. */
function centralDay() {
  try {
    return new Intl.DateTimeFormat('en-CA', { timeZone: 'America/Chicago', year: 'numeric', month: '2-digit', day: '2-digit' }).format(new Date());
  } catch (e) {
    return new Date().toISOString().slice(0, 10);
  }
}

/**
 * Whether this batch is this browser's first of the Central day: the server counts the visitor
 * from it, so there is no document per visitor. The day is marked before the batch goes, and
 * unmarked if it does not arrive, so the next batch carries the flag instead.
 */
function claimFirstOfDay() {
  const today = centralDay();
  const last = read(window.localStorage, DAY_KEY) || state.lastDay;
  if (last === today) return { first: false, undo: () => {} };
  const before = last || '';
  state.lastDay = today;
  write(window.localStorage, DAY_KEY, today);
  return {
    first: true,
    undo: () => {
      if (state.lastDay !== today) return;
      state.lastDay = before;
      try {
        if (before) window.localStorage.setItem(DAY_KEY, before);
        else window.localStorage.removeItem(DAY_KEY);
      } catch (e) { /* storage blocked */ }
    },
  };
}

/**
 * The visitor's country or US state and county, from functions-place/ (visitorPlace): asked a
 * second after the page opens, given 2.5 seconds, kept 12 hours (an empty answer too). Off while
 * the endpoint is empty, and for a browser marked as the team's. A failure is silent and not kept.
 * A county is kept only with its own state (cleanPlaceAnswer), and nothing finer ever is.
 */
function startPlace() {
  try {
    const endpoint = (window.__APR_TRACK_TEST__ && window.__APR_PLACE_TEST__) || PLACE_ENDPOINT;
    if (!endpoint || state.internal) return;
    let kept = null;
    try { kept = JSON.parse(read(window.localStorage, PLACE_KEY) || 'null'); } catch (e) { kept = null; }
    if (kept && typeof kept.at === 'number' && Date.now() - kept.at < PLACE_KEEP_MS && typeof kept.g === 'string') {
      const clean = cleanPlaceAnswer(kept);
      state.place = clean.g;
      state.county = clean.c;
      return;
    }
    setTimeout(() => {
      try {
        const controller = typeof AbortController === 'function' ? new AbortController() : null;
        const giveUp = controller ? setTimeout(() => controller.abort(), PLACE_WAIT_MS) : null;
        fetch(endpoint, { method: 'GET', credentials: 'omit', cache: 'no-store', ...(controller ? { signal: controller.signal } : {}) })
          .then((response) => (response && response.ok ? response.json() : null))
          .then((body) => {
            if (giveUp) clearTimeout(giveUp);
            if (!body || typeof body !== 'object') return;
            const { g, c } = cleanPlaceAnswer(body);
            state.place = g;
            state.county = c;
            write(window.localStorage, PLACE_KEY, JSON.stringify(c ? { g, c, at: Date.now() } : { g, at: Date.now() }));
          })
          .catch(() => { if (giveUp) clearTimeout(giveUp); });
      } catch (e) { /* no place: nothing changes */ }
    }, PLACE_START_MS);
  } catch (e) { /* no place: nothing changes */ }
}

function giveUpIfFailing() {
  state.failures += 1;
  // Back off while it fails (a blocked or undeployed endpoint, or no signal)
  // instead of trying every ten seconds; after a few in a row, stop altogether.
  state.retryAt = Date.now() + Math.min(30 * 60 * 1000, 30000 * 2 ** (state.failures - 1));
  if (state.failures >= GIVE_UP_AFTER) {
    state.off = true;
    state.queue = [];
    if (state.timer) clearInterval(state.timer);
  }
}

/**
 * Sends what is queued. `leaving` is the page closing or hiding: sendBeacon
 * survives that, a fetch may not, and nothing waits for either's answer.
 */
function flush({ leaving = false } = {}) {
  if (state.off || !state.queue.length) return;
  try { syncAuth(); } catch (e) { /* ignore */ }
  // A visit is queued before the guest sign-in has finished, so who it was is stamped as it goes out.
  for (const queued of state.queue) if (queued.t === 'visit' && !queued.who) queued.who = state.signedIn ? 'member' : 'guest';
  if (!leaving && Date.now() < state.retryAt) return;
  while (state.queue.length) {
    const events = state.queue.splice(0, MAX_BATCH);
    const day = claimFirstOfDay();
    const body = envelope(events, day.first);
    if (leaving) {
      try {
        if (navigator.sendBeacon && navigator.sendBeacon(ENDPOINT, body)) continue;
      } catch (e) { /* fall through to fetch */ }
    }
    try {
      fetch(ENDPOINT, {
        method: 'POST',
        body,
        keepalive: true,
        credentials: 'omit',
        // text/plain keeps this a "simple" request: no preflight, same as sendBeacon.
        headers: { 'Content-Type': 'text/plain;charset=UTF-8' },
      }).then((response) => {
        if (response && response.ok) state.failures = 0;
        // 400: the server read the batch and kept none of it (every event a kind it does not
        // know yet: a page one version ahead of the functions, say). Sending again would not
        // change that, so it is no reason to back off; the day's first batch is still to come.
        else if (response && response.status === 400) day.undo();
        else { day.undo(); giveUpIfFailing(); }
      }).catch(() => { day.undo(); giveUpIfFailing(); });
    } catch (e) { day.undo(); giveUpIfFailing(); }
    if (!leaving) break; // one batch per tick; the rest wait for the next
  }
}

function push(event) {
  if (state.off || !state.started) return;
  state.queue.push(event);
  if (state.queue.length > MAX_QUEUE) state.queue.splice(0, state.queue.length - MAX_QUEUE);
}

/** What the signed-in person is called, so no label that says it is ever reported. */
function personalTerms() {
  const user = auth.currentUser;
  if (!user || user.isAnonymous) return [];
  const email = user.email || '';
  return [user.displayName, email, email.split('@')[0]];
}

function onClick(event) {
  try {
    const target = event.target;
    const el = target && typeof target.closest === 'function' ? target.closest(PRESS_SELECTOR) : null;
    if (!el) return;
    const described = describeClick({
      dataTrack: el.getAttribute('data-track'),
      ariaLabel: el.getAttribute('aria-label'),
      title: el.getAttribute('title'),
      href: el.getAttribute('href'),
      isAnchor: el.tagName === 'A',
      // A Give button the sheet is holding (an answer still arriving) cancels the tap: not a press.
      ariaDisabled: el.getAttribute('aria-disabled') === 'true',
      // innerText keeps the space between a button's parts; textContent would run them together.
      getText: () => (el.children.length ? el.innerText : el.textContent) || '',
    }, (selector) => el.closest(selector) !== null, { avoid: personalTerms() });
    if (described) push(described);
  } catch (e) { /* a click is never broken by counting it */ }
}

/**
 * Keeps "signed in" and the ID token current. The token lets the server tell a
 * member from a guest (and a teammate from both) without trusting this page;
 * Firebase hands back its cached one, so this is almost never a network call.
 * Becoming a member mid-visit gets a fresh token, since the old one still says guest.
 */
function onAuth(user) {
  const was = state.signedIn;
  state.signedIn = Boolean(user && !user.isAnonymous);
  // Only a change after the first look is news: a member who opens the site is not "becoming" one.
  const changed = state.authSeen && state.signedIn !== was;
  state.authSeen = true;
  if (user && user.isAnonymous) state.sawGuest = true;
  if (state.signedIn && changed && state.sawGuest && state.started) push({ t: 'goal', name: 'signed_in' });
  if (user && typeof user.getIdToken === 'function') {
    user.getIdToken(changed).then((token) => { state.token = token; }).catch(() => { state.token = null; });
  } else {
    state.token = null;
  }
}

/** Linking a guest to an account does not always raise onIdTokenChanged, so look at who is signed in before each send. */
function syncAuth() {
  const user = auth.currentUser;
  if (user && Boolean(!user.isAnonymous) !== state.signedIn) onAuth(user);
}

/** Starts counting. Call once, from the app entry, after the app module has read its arrival link. */
export function startAnalytics() {
  if (state.started) return;
  try {
    // Read first: a team browser with Do Not Track on must still be able to mark itself
    // (?apr_internal=1) to preview the support sheet. It is a local setting, never sent.
    const internal = readInternalFlag();
    if (!shouldRun()) { state.off = true; return; }
    state.internal = internal;

    let visitor = read(window.localStorage, VISITOR_KEY);
    const knownVisitor = Boolean(visitor);
    if (!knownVisitor) { visitor = randomId(); write(window.localStorage, VISITOR_KEY, visitor); }
    let session = read(window.sessionStorage, SESSION_KEY);
    const newSession = !session;
    if (newSession) {
      session = randomId();
      write(window.sessionStorage, SESSION_KEY, session);
      write(window.sessionStorage, RETURNING_KEY, knownVisitor ? '1' : '0');
    }
    state.visitorId = visitor;
    state.sessionId = session;
    // "Returning" is a fact about when the session began: a reload of someone's
    // first visit is still a first visit.
    state.returning = read(window.sessionStorage, RETURNING_KEY) === '1';
    state.asks = parseInt(read(window.sessionStorage, ASKS_KEY), 10) || 0;
    state.attribution = buildAttribution(storedArrival(), referrerDomain(document.referrer, window.location.hostname));
    // A tab that has already sent its visit (a reload) keeps the place that visit went out with.
    if (!newSession) {
      try {
        const pinned = JSON.parse(read(window.sessionStorage, SESSION_PLACE_KEY) || 'null');
        if (pinned && typeof pinned === 'object') state.sessionPlace = cleanPlaceAnswer(pinned);
      } catch (e) { /* none kept: the place it has */ }
    }
    state.started = true;
    startPlace();

    onIdTokenChanged(auth, onAuth);

    if (newSession) {
      push(buildVisit({
        path: window.location.pathname,
        tz: (() => { try { return Intl.DateTimeFormat().resolvedOptions().timeZone; } catch (e) { return ''; } })(),
        lang: window.navigator.language,
        width: window.innerWidth,
        light: document.documentElement.getAttribute('data-theme') === 'day',
        attribution: state.attribution,
      }));
    }

    document.addEventListener('click', onClick, true);
    window.addEventListener('pagehide', () => { runLeavingHooks(); runHidingHooks(); flush({ leaving: true }); });
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') {
        runHidingHooks();
        flush({ leaving: true });
      }
    });
    state.timer = setInterval(() => flush(), SEND_EVERY_MS);
    setTimeout(() => flush(), FIRST_SEND_MS); // a short visit is on the books before it ends
  } catch (e) {
    state.off = true;
  }
}

/** The screen changed (App.js currentView). The same screen twice in a row counts once. */
export function trackView(name) {
  try {
    if (!state.started || state.off || !VIEWS.includes(name) || name === state.lastView) return;
    state.lastView = name;
    push({ t: 'view', name });
  } catch (e) { /* ignore */ }
}

/**
 * A question was sent (ChatView.sendMessage). Its words are not passed and not
 * kept; `n` is which question of this visit it is, so "asked at least one" can
 * be counted per ad. A "go deeper" re-ask is not a new question, and nor is the
 * question an ad opened with (`landing`): the app asks that one for them
 * (utils/campaign.js), so it is reported as `auto` and their own first
 * question is still their first.
 */
export function trackAsk({ mode, tier, rerun = false, landing = false } = {}) {
  try {
    if (!state.started || state.off) return; // opted out, blocked or not running: not even a counter is kept
    if (!rerun && !landing) {
      state.asks += 1;
      write(window.sessionStorage, ASKS_KEY, String(state.asks));
    }
    push({ t: 'ask', n: Math.max(1, state.asks), mode, tier, rerun: Boolean(rerun), ...(landing ? { auto: true } : {}) });
  } catch (e) { /* ignore */ }
}

/** A moment a click cannot show (an email gate appearing, say). See GOALS in analyticsCore.js. */
export function trackGoal(name) {
  try {
    if (state.started && !state.off && GOALS.includes(name)) push({ t: 'goal', name });
  } catch (e) { /* ignore */ }
}

/**
 * A Bible reference in an answer opened its passage (ChatView.openScripture):
 * the press "Scripture", once for each passage opened, however it was opened
 * (a tap, a click, a middle click, Enter). Counted here rather than as a
 * click, since a drag across a reference, a double-click on one or a tap that
 * only clears a selection opens nothing, and the count is what decides
 * whether references need a faint line to be found (scriptureLinks 2).
 */
export function trackScripture() {
  try {
    if (state.started && !state.off) push({ ...SCRIPTURE_PRESS });
  } catch (e) { /* ignore */ }
}

/**
 * A moment of one of the two A/B tests (utils/experiments.js): { x, k, arm, m }, each from a closed
 * list (utils/experimentsCore.js buildXpEvent). Never which question, never its words; the server
 * works the group out again from this browser's own visitor id.
 */
export function trackXp(moment) {
  try {
    if (!state.started || state.off) return;
    const event = buildXpEvent(moment);
    if (event) push(event);
  } catch (e) { /* ignore */ }
}

/**
 * A moment of a reading of Rob's voice (utils/voice.js SpeechPlayer): { ph: 'req', src },
 * { ph: 'play', w } or { ph: 'end', by, sec }. Counts only, from closed lists (analyticsCore.js
 * buildVoiceEvent): never which answer, never its words.
 */
export function trackVoice(moment) {
  try {
    if (!state.started || state.off) return;
    const event = buildVoiceEvent(moment);
    if (event) push(event);
  } catch (e) { /* ignore */ }
}
